import { useMemo } from 'react'
import { IProfile } from '@/types/profile'
import { resolveBrandingLayer } from '@/cinematics/utils/resolveBrandingLayer'
import { toTeamTheme } from '@/utils/teamBranding'
import { useEnrichedFavoriteTeam } from '@/hooks/teams/useEnrichedFavoriteTeam'

import settings from '@/settings/public'

const isTeamBrandingEnabled = settings.features?.teamBranding ?? false

interface UseBrandingLayerParams {
  eligibleBrandingTag: string | null
  isBrandingResolving: boolean
  profile?: IProfile
}

/**
 * Thin hook around the pure resolver — takes signals already derived by the
 * caller (AppProvider) rather than re-deriving identity/branding-tag itself,
 * so there is a single call site for each of those upstream hooks.
 */
export function useBrandingLayer({ eligibleBrandingTag, isBrandingResolving, profile }: UseBrandingLayerParams) {
  const enrichedTeam = useEnrichedFavoriteTeam(profile)
  const teamTheme = useMemo(() => toTeamTheme(enrichedTeam), [enrichedTeam])

  return useMemo(
    () => resolveBrandingLayer({
      isEnabled: isTeamBrandingEnabled,
      eligibleBrandingTag,
      isBrandingResolving,
      favoriteTeam: enrichedTeam,
      teamTheme,
    }),
    [eligibleBrandingTag, isBrandingResolving, enrichedTeam, teamTheme],
  )
}
